export type PageLoadingVariant =
  | 'home-logged-in'
  | 'home-logged-out'
  | 'browse'
  | 'practice-area'
  | 'industry'
  | 'credit'
  | 'credit-information'
  | 'jurisdiction'
  | 'generic';

interface PageLoadingVariantOptions {
  contentType?: string;
  isAuthenticated?: boolean;
  pathname?: string;
  search?: string;
}

const pageTypeVariants: Record<string, PageLoadingVariant> = {
  CreditClassPage: 'credit-information',
  CreditFormatPage: 'credit-information',
  CreditInfoRiverPage: 'credit-information',
  CreditInfoTypePage: 'credit-information',
  CreditPlanParentPage: 'credit',
  IndustryNode: 'industry',
  JurisdictionCreditInfoPage: 'jurisdiction',
  PracticeAreaPage: 'practice-area',
  SubjectNode: 'practice-area'
};

const pathnameVariants: Array<[RegExp, PageLoadingVariant]> = [
  [/\/(?:browse\/)?practice-areas?(?:\/|$)/, 'practice-area'],
  [/\/(?:browse\/)?industr(?:y|ies)(?:\/|$)/, 'industry'],
  [/\/credit-info\/credit-information\/cle(?:\/|$)/, 'jurisdiction'],
  [/\/credit-info(?:\/|$)/, 'credit-information'],
  [/\/credit-information(?:\/|$)/, 'credit-information'],
  [/\/(?:browse\/)?credits?(?:\/|$)/, 'credit']
];

/**
 * Selects the closest loading layout before CMS page data is available.
 */
export const getPageLoadingVariant = ({
  contentType,
  isAuthenticated = false,
  pathname = '',
  search = ''
}: PageLoadingVariantOptions): PageLoadingVariant => {
  const normalizedPathname = pathname.toLowerCase().replace(/\/+$/, '') || '/';

  if (contentType === 'StartPage' || normalizedPathname === '/') {
    return isAuthenticated ? 'home-logged-in' : 'home-logged-out';
  }

  if (contentType && pageTypeVariants[contentType]) {
    return pageTypeVariants[contentType];
  }

  if (normalizedPathname === '/programs/browse') {
    const activeTab = new URLSearchParams(search).get('activetab')?.toLowerCase();

    if (activeTab === 'credits' || activeTab === 'credit plans') return 'credit';
    if (activeTab === 'practice areas') return 'practice-area';
    if (activeTab === 'industries') return 'industry';

    return 'browse';
  }

  return pathnameVariants.find(([pattern]) => pattern.test(normalizedPathname))?.[1] ?? 'generic';
};
